import React from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { Link } from 'react-router-dom'
import Tooltip from 'components/Tooltip'
import rmStyles from './ReleaseModal.module.scss'
import { withTranslation } from 'react-i18next'
import {
  SongListItem,
  ImageWithFallback,
  RoundShadowButton,
  ModalWrapper,
  AspectRatioContainer,
  PopupProvider,
  AddToPlaylistTransformer
} from 'components'
import {
  playNextLogo,
  playAtEndLogo,
  placeholderLogo,
  infoLogo,
  playLogo,
  flagLogo,
  archiveLogo,
  plusLogo,
  closeLogo,
  blueCheckLogo
} from 'images'
import ReleaseHelper from 'models/Release'
import { Button, Modal } from '@dsny/dsny-component-library'
import { theme } from '../../../tailwind.theme'
import { isReleaseFlagged, thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import FlaggedActions, { thunks as FlaggedThunks } from 'reducers/Flagged'
import NotificationActions from 'reducers/Notification'
import ArchivedActions from 'reducers/Archived'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import { thunks as MarketThunks } from 'reducers/Market'
import {
  NotificationType,
  AnchorPositions,
  PopupDirections,
  marketOptions
} from 'constants/enums'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import { trackEvent } from 'utils/segmentService'

import { connect } from 'react-redux'
import { ReleasesService } from 'services'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'

const mapStateToProps = ({ options, global }) => ({
  markets: options.markets,
  isIpadView: global.isIpadView
})

@connect(mapStateToProps)
class ReleaseModal extends React.Component {
  static propTypes = {
    release: PropTypes.object,
    onClickCloseButton: PropTypes.func,
    dispatch: PropTypes.func,
    t: PropTypes.func.isRequired,
    isIpadView: PropTypes.bool,
    archiveToFilterEnabled: PropTypes.bool
  }

  state = {
    didTapPlayNext: false,
    didTapPlayLater: false,
    selectedSong: null
  }
  // Modal View event is handled automatically by DSNY Modal component
  // via analyticsModalType="release_modal"

  handleFlagButtonClick = () => {
    const { release, dispatch } = this.props
    const currentValue = isReleaseFlagged(release)

    trackEvent('Click', {
      source: 'release_modal',
      type: 'flag',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || '',
      action: currentValue ? 'unflag' : 'flag'
    })

    dispatch(
      FlaggedThunks.flag(
        release.releaseid,
        !currentValue,
        () => {
          const updatedRelease = {
            ...release,
            flagged: !currentValue,
            songs: release.songs?.map((song) => ({
              ...song,
              flagged: !currentValue
            }))
          }

          dispatch(ReleaseActions.toggleReleaseInModal(updatedRelease))
          dispatch(ReleaseActions.updateRelease(updatedRelease))

          if (!currentValue) {
            dispatch(FlaggedActions.addFlaggedRelease(updatedRelease))
          }

          dispatch(
            NotificationActions.broadcastMessage(
              `${updatedRelease.artist} - ${updatedRelease.title}`,
              updatedRelease.flagged
                ? NotificationType.flag
                : NotificationType.unFlag
            )
          )
        },
        () => {}
      )
    )
  }

  handleDownloadRelease = async (release, format) => {
    const { dispatch } = this.props

    trackEvent('Click', {
      source: 'release_modal',
      type: 'download_all',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || '',
      format: format,
      track_count: String(release.songs?.length || 0)
    })

    const releaseDetail = await ReleasesService.getDetail(release.releaseid)

    if (!isEmpty(releaseDetail.data)) {
      dispatch(
        DownloadsThunks.addReleaseToDownloadQueue(
          release,
          format,
          'release_modal'
        )
      )
    }
  }

  handlePlayRelease = () => {
    const { dispatch, release } = this.props
    dispatch(
      PlayQueueThunks.playRelease(release.releaseid, {
        source: PLAY_SOURCES.RELEASE_MODAL,
        trigger: PLAY_TRIGGERS.PLAY_ALL
      })
    )

    trackEvent('Click', {
      source: 'release_modal',
      type: 'play',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || '',
      track_count: String(release.songs?.length || 0)
    })
  }

  handlePlayNext = () => {
    const { didTapPlayNext } = this.state
    if (!didTapPlayNext) {
      const { dispatch, release } = this.props
      dispatch(PlayQueueThunks.insertReleaseNext(release.releaseid))

      trackEvent('Click', {
        source: 'release_modal',
        type: 'play_next',
        release_id: String(release.releaseid),
        artist: release.artist || '',
        title: release.title || '',
        item_type: 'release',
        track_count: String(release.songs?.length || 0)
      })

      this.setState({ didTapPlayNext: true })
    }
  }

  handlePlayAtBottom = () => {
    const { didTapPlayLater } = this.state
    if (!didTapPlayLater) {
      const { dispatch, release } = this.props
      dispatch(PlayQueueThunks.insertReleaseAtBottom(release.releaseid))

      trackEvent('Click', {
        source: 'release_modal',
        type: 'play_later',
        release_id: String(release.releaseid),
        artist: release.artist || '',
        title: release.title || '',
        item_type: 'release',
        track_count: String(release.songs?.length || 0)
      })

      this.setState({ didTapPlayLater: true })
    }
  }

  handleArchiveButtonClick = () => {
    const { release, dispatch, archiveToFilterEnabled } = this.props
    const currentValue = release.archived

    trackEvent('Click', {
      source: 'release_modal',
      type: 'archive',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || '',
      action: currentValue ? 'restore' : 'archive'
    })

    dispatch(
      ReleaseThunks.archive(
        release.releaseid,
        !currentValue,
        () => {
          const shouldUnflagOnArchive =
            !currentValue && isReleaseFlagged(release)
          const updatedRelease = {
            ...release,
            archived: !currentValue,
            flagged: shouldUnflagOnArchive ? false : release.flagged,
            songs: shouldUnflagOnArchive
              ? release.songs?.map((song) => ({
                  ...song,
                  flagged: false
                }))
              : release.songs
          }

          dispatch(ReleaseActions.toggleReleaseInModal(updatedRelease))
          dispatch(ReleaseActions.updateRelease(updatedRelease))

          dispatch(
            NotificationActions.broadcastMessage(
              `${updatedRelease.artist} - ${updatedRelease.title}`,
              updatedRelease.archived
                ? NotificationType.archive
                : NotificationType.unArchive
            )
          )

          if (archiveToFilterEnabled) {
            dispatch(ReleaseActions.removeRelease(updatedRelease))
          } else if (!currentValue) {
            dispatch(ReleaseActions.removeRelease(updatedRelease))
            dispatch(ArchivedActions.addArchivedRelease(updatedRelease))
          } else {
            dispatch(ReleaseActions.addRelease(updatedRelease))
            dispatch(ArchivedActions.removeArchivedRelease(updatedRelease))
          }

          if (shouldUnflagOnArchive) {
            this.handleFlagButtonClick()
          }
        },
        () => {},
        archiveToFilterEnabled
      )
    )
  }

  setSelectedSong = (song) => this.setState({ selectedSong: song })

  handleInfoClick = () => {
    const { release, onClickCloseButton } = this.props

    trackEvent('Click', {
      source: 'release_modal',
      type: 'info',
      release_id: String(release.releaseid),
      artist: release.artist || '',
      title: release.title || ''
    })

    if (onClickCloseButton) {
      onClickCloseButton()
    }
  }

  renderInfo = () => {
    const { release, t } = this.props
    const { didTapPlayLater, didTapPlayNext } = this.state
    const { thumbnail, artist, title, labels, duration, candownload } = release
    const brandLogo = labels.length > 0 ? labels[0].logo : null
    const songsInfoStr = ReleaseHelper.getSongsInfoStr(release)
    let displayLogo = brandLogo ? (
      <ImageWithFallback
        src={brandLogo}
        fallback={placeholderLogo}
        className="w-20 max-h-full"
        alt="brand logo"
      />
    ) : (
      <div>{release.labels[0].name}</div>
    )
    return (
      <div className="bg-modalGrey flex relative flex-row justify-between items-center pl-6 pt-4 pr-10 h-64 pb-24 rounded-t">
        <div className="flex flex-row justify-start items-center">
          <AspectRatioContainer widthClassName="w-32">
            <ImageWithFallback
              className="w-full h-full object-cover"
              src={thumbnail}
              fallback={placeholderLogo}
              alt="album cover"
            />
          </AspectRatioContainer>
          <div className="flex flex-col justify-center h-28 ml-4">
            <div className="flex">
              <p className="text-xl mb-1">{artist}</p>
              {this.renderMarkets()}
            </div>

            <p className="text-sm mb-1">{title}</p>

            <p className="text-sm">{songsInfoStr}</p>
            <div className="mt-2">
              {candownload && (
                <>
                  <div className="flex flex-row">
                    <div className="mr-4">
                      <Tooltip
                        hasArrow={false}
                        placement="top"
                        text={this.props.t('release-info.download-all-wav')}
                        containerStyles={{ width: 276, whiteSpace: 'normal' }}
                        theme={theme}
                      >
                        <Button
                          type="button"
                          className={`w-full lg:h-10 h-8 btn shadow-md self-center ${rmStyles['submit-btn']}`}
                          setting="player"
                          variant="primary"
                          onClick={() =>
                            this.handleDownloadRelease(release, 'wav')
                          }
                        >
                          <div className="flex flex-row justify-between">
                            <h1>{t('Download all (WAV)')}</h1>
                          </div>
                        </Button>
                      </Tooltip>
                    </div>
                    <div className="mr-4">
                      <Tooltip
                        hasArrow={false}
                        placement="top"
                        text={this.props.t('release-info.download-all-mp3')}
                        containerStyles={{ width: 276, whiteSpace: 'normal' }}
                        theme={theme}
                      >
                        <Button
                          type="button"
                          className={`w-full lg:h-10 h-8 btn shadow-md self-center bg-transparent ${rmStyles['submit-btn']}`}
                          setting="player"
                          variant="secondary"
                          onClick={() =>
                            this.handleDownloadRelease(release, 'mp3')
                          }
                        >
                          <div className="flex flex-row justify-between">
                            <h1>{t('Download all (MP3)')}</h1>
                          </div>
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {displayLogo}
      </div>
    )
  }

  renderButtons = () => {
    const { release, dispatch, onClickCloseButton, t } = this.props
    const { didTapPlayLater, didTapPlayNext } = this.state
    const { archived, duration } = release
    const flagged = isReleaseFlagged(release)
    return (
      <div className="absolute h-24 w-full px-6 top-0 mt-38">
        <div className="flex flex-row justify-between items-center w-full h-full">
          <div className="flex flex-row justify-between items-center w-40">
            <div className="mt-6">
              <RoundShadowButton
                icon={playLogo}
                disabled={duration == 0}
                cicleWidthClass="w-12"
                imageWidthClass="w-4"
                className="mt-4"
                onClick={this.handlePlayRelease}
              />
            </div>
            <div className="flex flex-col justify-center h-28">
              <div className="flex flex-row justify-start items-center py-2">
                <RoundShadowButton
                  removeTextComponent
                  disabled={duration == 0}
                  icon={didTapPlayNext ? blueCheckLogo : playNextLogo}
                  cicleWidthClass="w-6"
                  imageWidthClass="w-3"
                  onClick={this.handlePlayNext}
                />
                <p className="text-xs px-2">{t('general.play-next')}</p>
              </div>
              <div className="flex flex-row justify-start items-center">
                <RoundShadowButton
                  removeTextComponent
                  disabled={duration == 0}
                  icon={didTapPlayLater ? blueCheckLogo : playAtEndLogo}
                  cicleWidthClass="w-6"
                  imageWidthClass="w-3"
                  onClick={this.handlePlayAtBottom}
                />
                <p className="text-xs px-2">{t('general.play-later')}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-row justify-between items-center w-60">
            <Link to={'/releases/releaseInfo/' + release.releaseid}>
              <RoundShadowButton
                text={t('general.info')}
                icon={infoLogo}
                cicleWidthClass="w-10"
                imageWidthClass="w-1.5"
                totalWidthClass="w-16"
                onClick={this.handleInfoClick}
              />
            </Link>
            <RoundShadowButton
              icon={flagLogo}
              cicleWidthClass="w-10"
              imageWidthClass="w-3"
              totalWidthClass="w-16"
              text={`${flagged ? t('general.unflag') : t('general.flag')}`}
              disabled={archived}
              onClick={this.handleFlagButtonClick}
            />
            <RoundShadowButton
              icon={archiveLogo}
              cicleWidthClass="w-10"
              imageWidthClass="w-4"
              totalWidthClass="w-16"
              text={`${archived ? t('general.restore') : t('general.archive')}`}
              onClick={this.handleArchiveButtonClick}
            />
            {this.renderAddToButton()}
          </div>
        </div>
      </div>
    )
  }

  renderAddToButton = () => {
    const { release, t } = this.props
    return (
      <PopupProvider
        tapOutSideToDismiss
        anchorPosition={AnchorPositions.bottomCenter}
        popupDirection={PopupDirections.down}
        renderTrigger={({ setOnOff }) => (
          <RoundShadowButton
            icon={plusLogo}
            cicleWidthClass="w-10"
            imageWidthClass="w-4"
            totalWidthClass="w-16"
            text={t('general.addto')}
            onClick={(event) => {
              event.stopPropagation()
              setOnOff(true)
            }}
          />
        )}
        renderChildren={({ setOnOff }) => (
          <div className="bg-white rounded text-black pt-2 overflow-y-auto max-h-64 scrollbar-blue w-40 shadow-lg">
            <AddToPlaylistTransformer
              isOpen
              release={release}
              setOnOff={setOnOff}
              trackingSource="Release_Modal"
            />
          </div>
        )}
      />
    )
  }

  renderMarkets = () => {
    const { release, markets, dispatch } = this.props
    if (!release?.markettags?.length) {
      return null
    }

    const marketIcons = release.markettags.map((item) => {
      for (let i = 0; i < (markets?.length ?? 0); i++) {
        if (markets[i].id === item) {
          const url = dispatch(
            MarketThunks.getMarketLogoSrc(
              markets[i].icon,
              marketOptions.darkBlue,
              marketOptions.two
            )
          )
          return (
            <ImageWithFallback
              key={`${item}`}
              className="w-6 h-6 mt-.5 mb-2 ml-3"
              src={url}
              alt={item}
            />
          )
        }
      }
      return ''
    })
    return marketIcons
  }

  renderTrackList = () => {
    const { release, isIpadView } = this.props
    const { selectedSong } = this.state
    const { songs } = release
    const songCells = songs.map((song, index) => (
      <SongListItem
        key={`${song.trackid}`}
        index={index}
        song={song}
        release={release}
        selectedSong={selectedSong}
        isIpadView={isIpadView}
        setSelectedSong={this.setSelectedSong}
        trackingSource="Release_Modal"
        playSource={PLAY_SOURCES.RELEASE_MODAL}
      />
    ))
    return (
      <div className="pt-4 bg-white flex flex-1 rounded-b">
        <div className="h-64 w-full overflow-auto py-3 scrollbar-black">
          {songCells}
        </div>
      </div>
    )
  }

  renderCloseButton = () => {
    const { onClickCloseButton } = this.props
    return (
      <button
        type="button"
        className="flex justify-around items-center w-6 h-6 absolute mt-1 mr-2 top-0 right-0 focus:outline-none"
        onClick={onClickCloseButton}
      >
        <img src={closeLogo} className="w-1/2 h-1/2" alt="close button" />
      </button>
    )
  }

  handleCloseModal = () => {
    const { onClickCloseButton, dispatch } = this.props
    // Modal close event is handled automatically by DSNY Modal component

    if (onClickCloseButton) {
      onClickCloseButton()
      return
    }
    dispatch(ReleaseActions.toggleReleaseInModal(null))
  }

  render() {
    return (
      <Modal
        isOpen
        onClose={this.handleCloseModal}
        analyticsModalType="release_modal"
        trackEvent={trackEvent}
        containerStyles={{
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <ModalWrapper
          className="w-full h-1/2 relative z-50 rounded mx-auto"
          onClickOutside={this.handleCloseModal}
        >
          {this.renderInfo()}
          {this.renderButtons()}
          {this.renderTrackList()}
          {this.renderCloseButton()}
        </ModalWrapper>
      </Modal>
    )
  }
}
const ReleaseModalWithArchiveGate = (props) => {
  const { enabled } = useArchiveToFilter()
  return <ReleaseModal {...props} archiveToFilterEnabled={enabled} />
}

export default withTranslation()(ReleaseModalWithArchiveGate)
